import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { message } from 'antd';
import {
  HomeOutlined,
  PlusOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  UserOutlined,
  ShopOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
  TagsOutlined,
  PieChartOutlined,
  GiftOutlined,
  CalendarOutlined,
  DollarOutlined,
  TransactionOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { ExpenseTransactionModalMobile } from '../expenses/ExpenseTransactionModalMobile';
import { AddCategoriesModalMobile } from '../expenses/AddCategoriesModalMobile';
import { AddGiftsModalMobile } from '../gifts/AddGiftsModalMobile';
import { IncomeTransactionModalMobile } from '../income/IncomeTransactionModalMobile';
import { useAuthStore } from '../../stores/authStore';
import { Button } from 'antd';
import Logo from './Logo';

const BottomNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();

  // States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showMorePopover, setShowMorePopover] = useState(false);
  const [showReportDrawer, setShowReportDrawer] = useState(false);

  const moreMenuRef = useRef<HTMLButtonElement>(null);

  // Queries

  // Handlers

  const handleCategorySuccess = async () => {
    try {
      await queryClient.invalidateQueries('expense-categories');
      setShowCategoryModal(false);
      message.success('Tạo danh mục chi tiêu thành công');
      // Refresh màn hình gốc sau khi thêm thành công
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật dữ liệu');
    }
  };

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMorePopover(false);
      }
    };
    if (showMorePopover) {
      document.addEventListener('click', handleClick);
    }
    return () => document.removeEventListener('click', handleClick);
  }, [showMorePopover]);

  // Xác định active tab dựa vào current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    // Kiểm tra nếu đang ở menu trong nhóm "Khác" thì không highlight các tab chính
    if (isInMoreMenu()) return '';
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/gifts')) return 'gifts';
    return '';
  };

  // Xác định xem có đang ở menu trong nhóm "Khác" không
  const isInMoreMenu = () => {
    const path = location.pathname;
    return path === '/expenses' || path === '/expenses/categories' || path === '/gifts/list' || path === '/gifts/givers' || path === '/gifts/events' || path === '/income/wallet' || path === '/income' || path === '/youtube';
  };

  // Xác định active menu trong nhóm "Khác"
  const getActiveMoreMenu = () => {
    const path = location.pathname;
    if (path === '/expenses') return 'expenses';
    if (path === '/expenses/categories') return 'categories';
    if (path === '/gifts/list') return 'gifts-list';
    if (path === '/gifts/givers') return 'gifts-givers';
    if (path === '/gifts/events') return 'events';
    if (path === '/income/wallet') return 'wallet';
    if (path === '/income') return 'income';
    if (path === '/youtube') return 'youtube';
    return '';
  };

  const activeTab = getActiveTab();

  const buttonStyle = {
    pointerEvents: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    background: 'none',
    border: 'none',
    padding: '4px 0',
    cursor: 'pointer',
    minWidth: '48px',
    transition: 'color 0.3s ease'
  };

  const iconStyle = (isActive: boolean) => ({
    fontSize: '24px',
    marginBottom: '4px',
    color: isActive ? '#FF89A1' : '#999'
  });

  const labelStyle = (isActive: boolean) => ({
    fontSize: '12px',
    marginTop: '2px',
    color: isActive ? '#FF89A1' : '#999'
  });

  const handleCreateNew = (type: string) => {
    setShowCreateModal(false);
    switch (type) {
      case 'expense':
        setShowExpenseModal(true);
        break;
      case 'category':
        setShowCategoryModal(true);
        break;
      case 'gift':
        setShowGiftModal(true);
        break;
      case 'income':
        setShowIncomeModal(true);
        break;
    }
  };

  const createMenuItems = [
    { 
      title: 'Tạo mới chi tiêu',
      type: 'expense',
      icon: <WalletOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
      color: '#FFE8EC',
      borderColor: '#FF89A1'
    },
    { 
      title: 'Tạo mới thu nhập',
      type: 'income',
      icon: <DollarOutlined style={{ fontSize: '20px', color: '#10B981' }} />,
      color: '#E8F5E9',
      borderColor: '#10B981'
    }
  ];

  const moreMenuItems = [
    {
      groupTitle: 'Quản lý thu chi',
      items: [
        {
          title: 'Chi tiêu',
          icon: <WalletOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
          path: '/expenses'
        },
        {
          title: 'Thu nhập',
          icon: <DollarOutlined style={{ fontSize: '20px', color: '#10B981' }} />,
          path: '/income'
        },
        {
          title: 'Danh mục chi tiêu',
          icon: <TagsOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
          path: '/expenses/categories'
        },
        {
          title: 'Quản lý ví',
          icon: <WalletOutlined style={{ fontSize: '20px', color: '#10B981' }} />,
          path: '/income/wallet'
        }
      ]
    },
    {
      groupTitle: 'Quản lý tiền mừng',
      items: [
        {
          title: 'Danh sách tiền mừng',
          icon: <GiftOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
          path: '/gifts/list'
        },
        {
          title: 'Khách mời',
          icon: <UserOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
          path: '/gifts/givers'
        },
        {
          title: 'Sự kiện',
          icon: <CalendarOutlined style={{ fontSize: '20px', color: '#FF89A1' }} />,
          path: '/gifts/events'
        }
      ]
    },
    {
      groupTitle: 'Tiện ích',
      items: [
        {
          title: 'YouTube Player',
          icon: <PlayCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />,
          path: '/youtube'
        }
      ]
    }
  ];



  const expenseMenuItems = [
    {
      title: 'Giao dịch chi tiêu',
      icon: <WalletOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      path: '/expenses'
    },
    {
      title: 'Danh mục chi tiêu',
      icon: <TagsOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      path: '/expenses/categories'
    },
    {
      title: 'Báo cáo chi tiêu',
      icon: <PieChartOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />,
      path: '/expenses/reports'
    }
  ];

  // Danh sách báo cáo
  const reportMenuItems = [
    {
      title: 'Báo cáo chi tiêu',
      path: '/expenses/reports',
    },
  ];

  // Avatar user
  const getUserAvatar = () => {
    const avatarUrl = user?.avatar;
    
    return (
      <div style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        overflow: 'hidden',
        background: '#f0f0f0',
        marginRight: -80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="User Avatar"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              // Fallback to UserOutlined icon if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = '<svg viewBox="64 64 896 896" fill="currentColor" width="24" height="24"><path d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.3-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.8-204.3C356.3 562.2 472 526 512 526s155.7 36.2 211.2 91.7C777.7 666.8 808.7 739.1 810.7 816.4c.1 4.3 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z"></path></svg>';
              }
            }}
          />
        ) : (
          <UserOutlined style={{ fontSize: '24px', color: '#999' }} />
        )}
      </div>
    );
  };

  return (
    <>
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        height: '96px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingBottom: 'env(safe-area-inset-bottom, 24px)',
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}>
        {/* Dashboard */}
        <button
          style={{
            ...buttonStyle,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={() => navigate('/dashboard')}
        >
          <HomeOutlined style={iconStyle(activeTab === 'dashboard')} />
          <span style={labelStyle(activeTab === 'dashboard')}>Dashboard</span>
        </button>

        {/* Tiền mừng */}
        <button
          style={{
            ...buttonStyle,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={() => navigate('/gifts')}
        >
          <GiftOutlined style={iconStyle(activeTab === 'gifts')} />
          <span style={labelStyle(activeTab === 'gifts')}>Tiền mừng</span>
        </button>

        {/* Nút tạo mới ở giữa */}
        <div style={{ ...buttonStyle, position: 'relative' }}>
          <button
            style={{
              pointerEvents: 'auto',
              position: 'absolute',
              top: '-68px', // Đẩy nút lên cao hơn khi menu cao hơn
              left: '50%',
              transform: 'translateX(-50%)',
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: '#FF89A1',
              border: '4px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 1, // Thêm để đảm bảo nút luôn nổi lên trên
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}
            onClick={() => setShowCreateModal(true)}
          >
            <PlusOutlined style={{ fontSize: '28px' }} />
          </button>
        </div>

        {/* Giao dịch */}
        <button
          style={{
            ...buttonStyle,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={() => navigate('/transactions')}
        >
          <TransactionOutlined style={iconStyle(activeTab === 'transactions')} />
          <span style={labelStyle(activeTab === 'transactions')}>Giao dịch</span>
        </button>

                 {/* Khác */}
         <button
           ref={moreMenuRef}
           style={{
             ...buttonStyle,
             userSelect: 'none',
             WebkitUserSelect: 'none',
             MozUserSelect: 'none',
             msUserSelect: 'none',
             WebkitTouchCallout: 'none',
             WebkitTapHighlightColor: 'transparent'
           }}
           onClick={() => setShowMorePopover(prev => !prev)}
         >
           <MenuOutlined style={iconStyle(showMorePopover || isInMoreMenu())} />
           <span style={labelStyle(showMorePopover || isInMoreMenu())}>Khác</span>
         </button>
      </nav>



      {/* Popover cho menu Khác */}
      {showMorePopover && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowMorePopover(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}>
            {/* User info + logout */}
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafbfc',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              justifyContent: 'space-between',
            }}>
              {getUserAvatar()}
              <div style={{ marginLeft: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: '#262626', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name || user?.username}</div>
                <div style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.username}</div>
              </div>
              <Button
                type="primary"
                danger
                size="small"
                style={{ 
                  borderRadius: 6, 
                  fontWeight: 600,
                  background: '#FF89A1',
                  borderColor: '#FF89A1',
                  color: 'white'
                }}
                onClick={() => { logout(); navigate('/login'); }}
              >
                Đăng xuất
              </Button>
            </div>

                         <div style={{ 
               padding: '16px',
               display: 'flex', 
               flexDirection: 'column', 
               gap: '16px',
               maxHeight: 'calc(85vh - 64px)',
               overflowY: 'auto',
               paddingBottom: 40
             }}>
               {moreMenuItems.map((group, groupIndex) => (
                 <div key={groupIndex}>
                                       <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#666',
                      marginBottom: '8px',
                      paddingLeft: '0px',
                      textAlign: 'left'
                    }}>
                      {group.groupTitle}
                    </div>
                   <div style={{
                     display: 'grid',
                     gridTemplateColumns: '1fr 1fr',
                     gap: '8px'
                   }}>
                     {group.items.map((item, itemIndex) => {
                       const isActive = location.pathname === item.path;
                       return (
                         <button
                           key={`${groupIndex}-${itemIndex}`}
                           style={{
                             width: '100%',
                             padding: '12px',
                             display: 'flex',
                             flexDirection: 'column',
                             alignItems: 'center',
                             gap: '8px',
                             background: isActive ? '#FFE8EC' : '#fff',
                             border: isActive ? '1px solid #FF89A1' : '1px solid #f0f0f0',
                             borderRadius: '12px',
                             cursor: 'pointer',
                             transition: 'all 0.3s ease',
                             boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                             userSelect: 'none',
                             WebkitUserSelect: 'none',
                             MozUserSelect: 'none',
                             msUserSelect: 'none',
                             WebkitTouchCallout: 'none',
                             WebkitTapHighlightColor: 'transparent',
                             minHeight: '80px',
                             justifyContent: 'center'
                           }}
                           onClick={() => {
                             navigate(item.path);
                             setShowMorePopover(false);
                           }}
                         >
                           <div style={{
                             width: '32px',
                             height: '32px',
                             borderRadius: '8px',
                             background: item.icon.props.style.color + '15',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center'
                           }}>
                             {React.cloneElement(item.icon, {
                               style: { 
                                 ...item.icon.props.style,
                                 fontSize: '16px'
                               }
                             })}
                           </div>
                           <span style={{ 
                             fontSize: '13px',
                             fontWeight: 500,
                             color: '#000000d9',
                             textAlign: 'center',
                             lineHeight: '1.2'
                           }}>{item.title}</span>
                         </button>
                       );
                     })}
                   </div>
                 </div>
               ))}
             </div>
          </div>
        </div>
      )}

      {/* Modal tạo mới */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateModal(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}>
            <div style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: 600,
                color: '#000000d9'
              }}>Tạo mới</h3>
              <button
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowCreateModal(false)}
              >
                <CloseOutlined style={{ fontSize: '16px', color: '#00000073' }} />
              </button>
            </div>
            <div style={{ 
              padding: '8px',
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              background: '#fff',
              paddingBottom: 40,
            }}>
              {createMenuItems.map((item) => (
                <button
                  key={item.type}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: item.color,
                    border: `1px solid ${item.borderColor}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => handleCreateNew(item.type)}
                >
                  {item.icon}
                  <span style={{ 
                    flex: 1,
                    textAlign: 'left',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#000000d9'
                  }}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

             {/* Các Modal tạo mới */}
             <ExpenseTransactionModalMobile
         isOpen={showExpenseModal}
         onClose={() => setShowExpenseModal(false)}
         onSuccess={() => {
           setShowExpenseModal(false);
           message.success('Thêm giao dịch chi tiêu thành công');
           // Chuyển về trang expenses sau khi thêm thành công
           navigate('/expenses');
         }}
       />

      <AddCategoriesModalMobile
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSuccess={handleCategorySuccess}
      />

      <AddGiftsModalMobile
        isOpen={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        onSuccess={() => {
          setShowGiftModal(false);
          message.success('Thêm tiền mừng thành công');
          // Chuyển về trang gifts sau khi thêm thành công
          navigate('/gifts/list');
        }}
        editingGift={null}
        events={[]}
        givers={[]}
        eventId={null}
        giverId={null}
      />

      <IncomeTransactionModalMobile
        isOpen={showIncomeModal}
        onClose={() => setShowIncomeModal(false)}
        onSuccess={() => {
          setShowIncomeModal(false);
          message.success('Thêm giao dịch thu nhập thành công');
          // Chuyển về trang income sau khi thêm thành công
          navigate('/income');
        }}
      />

      {/* Drawer danh sách báo cáo */}
      {showReportDrawer && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReportDrawer(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}>
            <div style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: 600,
                color: '#000000d9'
              }}>Danh sách báo cáo</h3>
              <button
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowReportDrawer(false)}
              >
                <CloseOutlined style={{ fontSize: '16px', color: '#00000073' }} />
              </button>
            </div>
            <div style={{ 
              padding: '16px',
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              background: '#fff',
              paddingBottom: 40
            }}>
              {reportMenuItems.map((item) => (
                <button
                  key={item.path}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#f6f8fa',
                    border: '1px solid #e6e6e6',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#262626',
                    transition: 'all 0.3s',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => {
                    navigate(item.path);
                    setShowReportDrawer(false);
                  }}
                >
                  <BarChartOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar; 